import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Tabs,
  Tab,
  Grid,
  IconButton,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Pagination,
  CircularProgress,
  Alert,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import InfoIcon from "@mui/icons-material/Info";
import moment from "moment";
import { urls } from "../../business/routes/urls";
import { baseAxios } from "../../services/api";
import { api } from "../../configs";
import { getUrl } from "../../services/helper";



// Tab components
const PaymentMethodsTab = ({ profileId }) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage] = useState(10);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPaymentMethods = async () => {
      try {
        setLoading(true);
        setError(null);
        const url = getUrl(
          api["storage-v2"]["recurrent-payment"]["payment-methods"],
          profileId
        );
        const response = await baseAxios().get(url);

        // Handle different response formats
        if (response.data) {
          const data = response.data;
          if (Array.isArray(data)) {
            setPaymentMethods(data);
          } else if (data.items && Array.isArray(data.items)) {
            setPaymentMethods(data.items);
          } else if (data.data && Array.isArray(data.data)) {
            setPaymentMethods(data.data);
          } else {
            setPaymentMethods([]);
          }
        }
      } catch (err) {
        console.error("Error fetching payment methods:", err);
        setError(err.message || "Failed to fetch payment methods");
        setPaymentMethods([]);
      } finally {
        setLoading(false);
      }
    };

    if (profileId) {
      fetchPaymentMethods();
    }
  }, [profileId]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage - 1); // Convert from 1-based to 0-based
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ py: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (paymentMethods.length === 0) {
    return (
      <Box sx={{ py: 3 }}>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          Showing payment methods 0 - 0 of 0
        </Typography>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "300px",
            color: "text.secondary",
          }}
        >
          <InfoIcon sx={{ fontSize: 48, color: "#1976d2", mb: 2 }} />
          <Typography variant="h6">
            No payment methods found for this user.
          </Typography>
        </Box>
      </Box>
    );
  }

  // Map payment method data to table format
  const mapPaymentMethodData = (method) => {
    return {
      id: method.id || method.payment_method_id || "",
      brand: method.brand || method.card_brand || method.type || "Unknown",
      type: method.type || method.payment_type || "Credit Card",
      last4: method.last4 || method.last_4 || method.last_four || method.card_last4 || "0000",
      expiry: method.expiry || method.expiry_date || method.expires_at || "N/A",
      status: method.status || method.payment_method_status || "Active",
      isDefault: method.isDefault || method.is_default || method.default || false,
    };
  };

  const startIndex = page * rowsPerPage;
  const endIndex = Math.min(startIndex + rowsPerPage, paymentMethods.length);
  const paginatedMethods = paymentMethods.slice(startIndex, endIndex).map(mapPaymentMethodData);

  return (
    <Box sx={{ py: 3 }}>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Showing payment methods {startIndex + 1} - {endIndex} of{" "}
        {paymentMethods.length}
      </Typography>
      <TableContainer component={Paper} elevation={0}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: "#F4F5F8" }}>
              <TableCell sx={{ fontWeight: 600, color: "#2B3033" }}>
                Type
              </TableCell>
              <TableCell sx={{ fontWeight: 600, color: "#2B3033" }}>
                Card Number
              </TableCell>
              <TableCell sx={{ fontWeight: 600, color: "#2B3033" }}>
                Expiry
              </TableCell>
              <TableCell sx={{ fontWeight: 600, color: "#2B3033" }}>
                Status
              </TableCell>
              <TableCell sx={{ fontWeight: 600, color: "#2B3033" }}>
                Default
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedMethods.map((method, index) => (
              <TableRow key={method.id || index} hover>
                <TableCell>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography variant="body2" fontWeight={500}>
                      {method.brand}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {method.type}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>•••• •••• •••• {method.last4}</TableCell>
                <TableCell>{method.expiry}</TableCell>
                <TableCell>
                  <Chip
                    label={method.status}
                    size="small"
                    color={
                      method.status === "Active" || method.status === "active"
                        ? "success"
                        : "default"
                    }
                  />
                </TableCell>
                <TableCell>
                  {method.isDefault && (
                    <Chip label="Default" size="small" color="primary" />
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <Box sx={{ display: "flex", justifyContent: "end", py: 2 }}>
          <Pagination
            count={Math.ceil(paymentMethods.length / rowsPerPage)}
            page={page + 1}
            onChange={handleChangePage}
            color="primary"
            variant="outlined"
            shape="rounded"
          />
        </Box>
      </TableContainer>
    </Box>
  );
};

const TransactionHistoryTab = ({ profileId }) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage] = useState(10);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchTransactions = async () => {
      try {
        setLoading(true);
        setError(null);
        const url = getUrl(
          api["storage-v2"]["recurrent-payment"].transactions,
          profileId
        );
        const response = await baseAxios().get(url);

        // Handle different response formats
        if (response.data) {
          const data = response.data;
          if (Array.isArray(data)) {
            setTransactions(data);
          } else if (data.items && Array.isArray(data.items)) {
            setTransactions(data.items);
          } else if (data.data && Array.isArray(data.data)) {
            setTransactions(data.data);
          } else {
            setTransactions([]);
          }
        }
      } catch (err) {
        console.error("Error fetching transactions:", err);
        setError(err.message || "Failed to fetch transactions");
        setTransactions([]);
      } finally {
        setLoading(false);
      }
    };

    if (profileId) {
      fetchTransactions();
    }
  }, [profileId]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage - 1); // Convert from 1-based to 0-based
  };

  // Map transaction data to table format
  const mapTransactionData = (transaction) => {
    // Format amount as currency
    const formatAmount = (amount) => {
      if (!amount) return "$0.00";
      const numAmount = parseFloat(amount);
      return isNaN(numAmount) ? `$${amount}` : `$${numAmount.toFixed(2)}`;
    };

    // Format payment method
    const formatPaymentMethod = () => {
      if (transaction.card_brand && transaction.card_last4) {
        return `${transaction.card_brand} •••• ${transaction.card_last4}`;
      }
      if (transaction.card_brand) {
        return transaction.card_brand;
      }
      if (transaction.card_last4) {
        return `•••• ${transaction.card_last4}`;
      }
      return transaction.payment_method || "Unknown";
    };

    // Format date (remove time if present)
    const formatDate = (dateString) => {
      if (!dateString) return "N/A";
      return dateString.split(" ")[0]; // Get only date part
    };

    return {
      id: transaction.id || transaction.anet_trans_id || transaction.transaction_id || "",
      date: formatDate(transaction.created_at || transaction.date || transaction.transaction_date),
      amount: formatAmount(transaction.amount),
      status: transaction.status || "Unknown",
      description: transaction.message || transaction.description || transaction.note || "N/A",
      paymentMethod: formatPaymentMethod(),
    };
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ py: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (transactions.length === 0) {
    return (
      <Box sx={{ py: 3 }}>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          Showing transactions 0 - 0 of 0
        </Typography>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "300px",
            color: "text.secondary",
          }}
        >
          <InfoIcon sx={{ fontSize: 48, color: "#1976d2", mb: 2 }} />
          <Typography variant="h6">
            No transactions found for this user.
          </Typography>
        </Box>
      </Box>
    );
  }

  const startIndex = page * rowsPerPage;
  const endIndex = Math.min(startIndex + rowsPerPage, transactions.length);
  const paginatedTransactions = transactions.slice(startIndex, endIndex).map(mapTransactionData);

  return (
    <Box sx={{ py: 3 }}>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Showing transactions {startIndex + 1} - {endIndex} of{" "}
        {transactions.length}
      </Typography>
      <TableContainer component={Paper} elevation={0}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: "#F4F5F8" }}>
              <TableCell sx={{ fontWeight: 600, color: "#2B3033" }}>
                Date
              </TableCell>
              <TableCell sx={{ fontWeight: 600, color: "#2B3033" }}>
                Description
              </TableCell>
              <TableCell sx={{ fontWeight: 600, color: "#2B3033" }}>
                Payment Method
              </TableCell>
              <TableCell sx={{ fontWeight: 600, color: "#2B3033" }}>
                Amount
              </TableCell>
              <TableCell sx={{ fontWeight: 600, color: "#2B3033" }}>
                Status
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedTransactions.map((transaction, index) => (
              <TableRow key={transaction.id || index} hover>
                <TableCell>{transaction.date}</TableCell>
                <TableCell>{transaction.description}</TableCell>
                <TableCell>{transaction.paymentMethod}</TableCell>
                <TableCell sx={{ fontWeight: 500 }}>
                  {transaction.amount}
                </TableCell>
                <TableCell>
                  <Chip
                    label={transaction.status}
                    size="small"
                    color={
                      transaction.status === "ok" || 
                      transaction.status === "Completed" || 
                      transaction.status === "completed" ||
                      transaction.status === "success"
                        ? "success"
                        : transaction.status === "Pending" || 
                          transaction.status === "pending" ||
                          transaction.status === "processing"
                        ? "warning"
                        : transaction.status === "failed" ||
                          transaction.status === "declined" ||
                          transaction.status === "error"
                        ? "error"
                        : "default"
                    }
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <Box sx={{ display: "flex", justifyContent: "end", py: 2 }}>
          <Pagination
            count={Math.ceil(transactions.length / rowsPerPage)}
            page={page + 1}
            onChange={handleChangePage}
            color="primary"
            variant="outlined"
            shape="rounded"
          />
        </Box>
      </TableContainer>
    </Box>
  );
};

const InvoiceTab = ({ profileId }) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage] = useState(10);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchInvoices = async () => {
      try {
        setLoading(true);
        setError(null);
        const url = getUrl(
          api["storage-v2"]["recurrent-payment"].invoices,
          profileId
        );
        const response = await baseAxios().get(url);

        // Handle different response formats
        if (response.data) {
          const data = response.data;
          if (Array.isArray(data)) {
            setInvoices(data);
          } else if (data.items && Array.isArray(data.items)) {
            setInvoices(data.items);
          } else if (data.data && Array.isArray(data.data)) {
            setInvoices(data.data);
          } else {
            setInvoices([]);
          }
        }
      } catch (err) {
        console.error("Error fetching invoices:", err);
        setError(err.message || "Failed to fetch invoices");
        setInvoices([]);
      } finally {
        setLoading(false);
      }
    };

    if (profileId) {
      fetchInvoices();
    }
  }, [profileId]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage - 1); // Convert from 1-based to 0-based
  };

  // Map invoice data to table format
  const mapInvoiceData = (invoice) => {
    // Format amount as currency
    const formatAmount = (amount) => {
      if (!amount) return "$0.00";
      const numAmount = parseFloat(amount);
      return isNaN(numAmount) ? `$${amount}` : `$${numAmount.toFixed(2)}`;
    };

    // Format date (remove time if present)
    const formatDate = (dateString) => {
      if (!dateString) return "N/A";
      return dateString.split(" ")[0]; // Get only date part
    };

    // Format description
    const formatDescription = () => {
      if (invoice.notes) return invoice.notes;
      if (invoice.billing_type) {
        // Convert billing_type to readable format
        const billingTypes = {
          last_charge: "Last Charge",
          recurring: "Recurring Payment",
          one_time: "One-time Payment",
        };
        return billingTypes[invoice.billing_type] || invoice.billing_type;
      }
      return invoice.invoice_number || "N/A";
    };

    return {
      id: invoice.invoice_number || invoice.id || invoice.invoice_id || "",
      date: formatDate(invoice.created_at || invoice.date || invoice.invoice_date),
      dueDate: formatDate(invoice.due_date || invoice.dueDate || invoice.due_at),
      amount: formatAmount(invoice.amount),
      status: invoice.status || "Unknown",
      description: formatDescription(),
    };
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ py: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (invoices.length === 0) {
    return (
      <Box sx={{ py: 3 }}>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          Showing invoices 0 - 0 of 0
        </Typography>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "300px",
            color: "text.secondary",
          }}
        >
          <InfoIcon sx={{ fontSize: 48, color: "#1976d2", mb: 2 }} />
          <Typography variant="h6">No invoices found for this user.</Typography>
        </Box>
      </Box>
    );
  }

  const startIndex = page * rowsPerPage;
  const endIndex = Math.min(startIndex + rowsPerPage, invoices.length);
  const paginatedInvoices = invoices.slice(startIndex, endIndex).map(mapInvoiceData);

  return (
    <Box sx={{ py: 3 }}>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Showing invoices {startIndex + 1} - {endIndex} of {invoices.length}
      </Typography>
      <TableContainer component={Paper} elevation={0}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: "#F4F5F8" }}>
              <TableCell sx={{ fontWeight: 600, color: "#2B3033" }}>
                Invoice ID
              </TableCell>
              <TableCell sx={{ fontWeight: 600, color: "#2B3033" }}>
                Date
              </TableCell>
              <TableCell sx={{ fontWeight: 600, color: "#2B3033" }}>
                Due Date
              </TableCell>
              <TableCell sx={{ fontWeight: 600, color: "#2B3033" }}>
                Description
              </TableCell>
              <TableCell sx={{ fontWeight: 600, color: "#2B3033" }}>
                Amount
              </TableCell>
              <TableCell sx={{ fontWeight: 600, color: "#2B3033" }}>
                Status
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedInvoices.map((invoice, index) => (
              <TableRow key={invoice.id || index} hover>
                <TableCell sx={{ fontWeight: 500, color: "#1976d2" }}>
                  {invoice.id}
                </TableCell>
                <TableCell>{invoice.date}</TableCell>
                <TableCell>{invoice.dueDate}</TableCell>
                <TableCell>{invoice.description}</TableCell>
                <TableCell sx={{ fontWeight: 500 }}>{invoice.amount}</TableCell>
                <TableCell>
                  <Chip
                    label={invoice.status}
                    size="small"
                    color={
                      invoice.status === "paid" || 
                      invoice.status === "Paid" ||
                      invoice.status === "completed"
                        ? "success"
                        : invoice.status === "pending" || 
                          invoice.status === "Pending" ||
                          invoice.status === "unpaid"
                        ? "warning"
                        : invoice.status === "failed" ||
                          invoice.status === "cancelled" ||
                          invoice.status === "cancelled"
                        ? "error"
                        : "default"
                    }
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <Box sx={{ display: "flex", justifyContent: "end", py: 2 }}>
          <Pagination
            count={Math.ceil(invoices.length / rowsPerPage)}
            page={page + 1}
            onChange={handleChangePage}
            color="primary"
            variant="outlined"
            shape="rounded"
          />
        </Box>
      </TableContainer>
    </Box>
  );
};


export const CustomerDetailsPage = () => {
  const { profileId } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);
  const [customerData, setCustomerData] = useState({
    name: "Loading...",
    email: "",
    phone: "",
    notes: "",
    customerId: "",
    office: "",
    jobNum: "",
    monthlyCharge: "",
    intoStorageDate: "",
    outOfStorageDate: "",
    moveSize: "",
    leadName: "",
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCustomerData = async () => {
      try {
        setLoading(true);
        // Fetch customer data from the list endpoint
        const response = await baseAxios().get(
          api["storage-v2"]["recurrent-payment"].list,
          {
            params: {
              page: 1,
              per_page: 1000, // Get all to find the specific customer
            },
          }
        );

        let customer = null;
        if (response.data) {
          const data = response.data;
          const items = data.items || data.data || (Array.isArray(data) ? data : []);
          customer = items.find(
            (item) =>
              String(item.zoho_id || item.id || item.profile_id || item.Id) === String(profileId)
          );
        }

        if (customer) {
          // Helper function to format date
          const formatDate = (dateString) => {
            if (!dateString || dateString === "-") return "";
            try {
              return moment(dateString).format("MM-DD-YYYY");
            } catch (e) {
              return dateString;
            }
          };

          // Map data from API response (fields may be capitalized)
          setCustomerData({
            name: customer.Name || customer.name || customer.customer_name || customer.full_name || "Unknown",
            email: customer.Owner?.email || customer.email || customer.customer_email || customer.Email || "",
            phone: customer.Phone || customer.phone || customer.phone_number || customer.customer_phone || "No phone number",
            notes: customer.Notes || customer.notes || "No notes yet",
            customerId: customer.id || customer.Id || customer.customer_id || customer.Customer_ID || profileId,
            office: customer.Office || customer.office || "",
            jobNum: customer.Job_Num || customer.job_num || "",
            monthlyCharge: customer.Monthly_Charge || customer.monthly_charge || "",
            intoStorageDate: formatDate(customer.Into_Storage_Date || customer.into_storage_date),
            outOfStorageDate: formatDate(customer.Out_of_Storage_Date || customer.out_of_storage_date),
            moveSize: customer.Move_Size || customer.move_size || "",
            leadName: customer.Lead?.name || customer.lead_name || "",
          });
        } else {
          setCustomerData({
            name: "Unknown Customer",
            email: "N/A",
            phone: "N/A",
            notes: "No notes yet",
            customerId: profileId || "N/A",
          });
        }
      } catch (err) {
        console.error("Error fetching customer data:", err);
        setCustomerData({
          name: "Error loading customer",
          email: "N/A",
          phone: "N/A",
          notes: "Failed to load data",
          customerId: "N/A",
        });
      } finally {
        setLoading(false);
      }
    };

    if (profileId) {
      fetchCustomerData();
    }
  }, [profileId]);

  useEffect(() => {
    document.title = `Customer Details - ${profileId}`;
  }, [profileId]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleBack = () => {
    navigate(urls.payments);
  };

  const tabs = [
    { label: "Payment methods", component: PaymentMethodsTab },
    { label: "Transaction History", component: TransactionHistoryTab },
    { label: "Invoice", component: InvoiceTab },
  ];

  const ActiveTabComponent = tabs[activeTab].component;

  return (
    <Box sx={{ minHeight: "100vh", backgroundColor: "#F8F9FA", py: 4 }}>
      <Container maxWidth="lg">
        {/* Header */}
        <Box
          sx={{
            mb: 4,
            display: "flex",
            alignItems: "center",
            gap: 2,
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            borderRadius: 3,
            p: 3,
            color: "#fff",
            boxShadow: "0 4px 20px rgba(102, 126, 234, 0.3)",
          }}
        >
          <IconButton
            onClick={handleBack}
            sx={{
              color: "#fff",
              backgroundColor: "rgba(255, 255, 255, 0.2)",
              "&:hover": {
                backgroundColor: "rgba(255, 255, 255, 0.3)",
              },
            }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="h4" fontWeight={700} sx={{ mb: 0.5 }}>
              {customerData.name}
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              Profile ID: {profileId}
            </Typography>
          </Box>
        </Box>

        <Card
          sx={{
            boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
            borderRadius: 3,
            mb: 3,
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              p: 2,
              color: "#fff",
            }}
          >
            <Typography variant="h6" fontWeight={600}>
              Customer Details
            </Typography>
          </Box>
          <CardContent sx={{ p: 3 }}>
            <Grid container spacing={3} sx={{ mb: 2 }}>
              <Grid item xs={12} md={6}>
                <Box
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    backgroundColor: "#f8f9fa",
                    border: "1px solid #e9ecef",
                    mb: 2,
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    gutterBottom
                    sx={{ fontWeight: 600, mb: 1 }}
                  >
                    Contact Information
                  </Typography>
                  <Typography variant="body1" color="#2B3033" sx={{ fontWeight: 500, mb: 1 }}>
                    {customerData.phone || "No phone number"}
                  </Typography>
                  {customerData.email && (
                    <Typography variant="body1" color="#667eea" sx={{ fontWeight: 500 }}>
                      {customerData.email}
                    </Typography>
                  )}
                </Box>
                {customerData.office && (
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      backgroundColor: "#f8f9fa",
                      border: "1px solid #e9ecef",
                      mb: 2,
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      color="text.secondary"
                      gutterBottom
                      sx={{ fontWeight: 600, mb: 1 }}
                    >
                      Office
                    </Typography>
                    <Chip
                      label={customerData.office}
                      sx={{
                        backgroundColor: "#e8eaf6",
                        color: "#667eea",
                        fontWeight: 600,
                      }}
                    />
                  </Box>
                )}
                {customerData.jobNum && (
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      backgroundColor: "#f8f9fa",
                      border: "1px solid #e9ecef",
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      color="text.secondary"
                      gutterBottom
                      sx={{ fontWeight: 600, mb: 1 }}
                    >
                      Job Number
                    </Typography>
                    <Typography variant="body1" color="#2B3033" sx={{ fontWeight: 600 }}>
                      {customerData.jobNum}
                    </Typography>
                  </Box>
                )}
              </Grid>
              <Grid item xs={12} md={6}>
                <Box
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    backgroundColor: "#f8f9fa",
                    border: "1px solid #e9ecef",
                    mb: 2,
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    gutterBottom
                    sx={{ fontWeight: 600, mb: 1 }}
                  >
                    Notes
                  </Typography>
                  <Typography variant="body2" color="#2B3033">
                    {customerData.notes}
                  </Typography>
                </Box>
                {customerData.monthlyCharge && (
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                      color: "#fff",
                      mb: 2,
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      sx={{ opacity: 0.9, mb: 1, fontWeight: 600 }}
                    >
                      Monthly Charge
                    </Typography>
                    <Typography variant="h5" fontWeight={700}>
                      ${parseFloat(customerData.monthlyCharge).toFixed(2)}
                    </Typography>
                  </Box>
                )}
                {customerData.moveSize && (
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      backgroundColor: "#f8f9fa",
                      border: "1px solid #e9ecef",
                      mb: 2,
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      color="text.secondary"
                      gutterBottom
                      sx={{ fontWeight: 600, mb: 1 }}
                    >
                      Move Size
                    </Typography>
                    <Typography variant="body1" color="#2B3033" sx={{ fontWeight: 600 }}>
                      {customerData.moveSize}
                    </Typography>
                  </Box>
                )}
                {(customerData.intoStorageDate || customerData.outOfStorageDate) && (
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      backgroundColor: "#f8f9fa",
                      border: "1px solid #e9ecef",
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      color="text.secondary"
                      gutterBottom
                      sx={{ fontWeight: 600, mb: 1 }}
                    >
                      Storage Dates
                    </Typography>
                    {customerData.intoStorageDate && (
                      <Typography variant="body2" color="#2B3033" sx={{ mb: 0.5 }}>
                        Into: {customerData.intoStorageDate}
                      </Typography>
                    )}
                    {customerData.outOfStorageDate && (
                      <Typography variant="body2" color="#2B3033">
                        Out: {customerData.outOfStorageDate}
                      </Typography>
                    )}
                  </Box>
                )}
              </Grid>
            </Grid>
            <Divider sx={{ my: 2 }} />
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Customer ID:
              </Typography>
              <Chip
                label={customerData.customerId}
                size="small"
                sx={{
                  backgroundColor: "#e3f2fd",
                  color: "#1976d2",
                  fontWeight: 600,
                }}
              />
            </Box>
          </CardContent>
        </Card>

        {/* Tabs */}
        <Card
          sx={{
            boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
            borderRadius: 3,
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              borderBottom: 1,
              borderColor: "divider",
              backgroundColor: "#fafbfc",
            }}
          >
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              sx={{
                "& .MuiTab-root": {
                  textTransform: "none",
                  fontWeight: 600,
                  minHeight: 56,
                  fontSize: "0.95rem",
                  "&:hover": {
                    color: "#667eea",
                  },
                },
                "& .Mui-selected": {
                  color: "#667eea",
                },
                "& .MuiTabs-indicator": {
                  backgroundColor: "#667eea",
                  height: 3,
                },
              }}
            >
              {tabs.map((tab, index) => (
                <Tab key={index} label={tab.label} />
              ))}
            </Tabs>
          </Box>
          <CardContent sx={{ p: 3 }}>
            <ActiveTabComponent profileId={profileId} />
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
};
